import React, { useEffect, useState, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { PageHeader, Button, Spin, Typography, Row, Col, Space } from 'antd';

import ReactHtmlParser from 'react-html-parser';
import Markdown from 'markdown-to-jsx';
import { useReactToPrint } from 'react-to-print';

import { handleApiError, MyBreadcrumb, accessControls } from 'utils';
import { knowledgeBaseViewListByIdService } from 'services/knowledge_base';
import { usersGetLoginClientService } from 'services/user';

import {
  galleryViewByIdService,
  photoListByGalleryIdService,
} from 'services/gallery';

import { PrinterOutlined, EditOutlined } from '@ant-design/icons';
import { Helmet } from 'react-helmet';
import Gallery from 'react-grid-gallery';

const { Title } = Typography;

export default function KnowledgeBaseViewPostDetail(props) {
  const history = useHistory();
  const { id } = props.match.params;
  const [isLoading, setIsLoading] = useState(false);
  const [post, setPost] = useState({});
  const [gallery, setGallery] = useState([]);
  const componentRef = useRef(null);
  const [permissions, setPermissions] = useState([]);
  const [activeClient, setActiveClient] = useState('');

  useEffect(() => {
    usersGetLoginClientService()
      .then((response) => {
        if (response.data.items && response.data.items.length > 0) {
          setActiveClient(response.data.items[0]);
        }
      })
      .catch((error) => {
        handleApiError(error);
      });
  }, []);

  useEffect(() => {
    loadPermissions();
  }, []);

  useEffect(() => {
    loadPost();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadPermissions = () => {
    //********START  ACCESS CONTROL******** */
    accessControls.fetch().then((permissions) => {
      setPermissions(permissions);
    });
    //********END ACCESS CONTROL******** */
  };

  const loadPost = () => {
    setIsLoading(true);
    knowledgeBaseViewListByIdService(id)
      .then((response) => {
        setIsLoading(false);
        //handleApiSuccess(response);
        if (response.data.items && response.data.items.length > 0) {
          const item = response.data.items[0];
          setPost(item);
          if (item.gallery) {
            loadGallery(item.gallery);
            //loadGalleryPhotos(item.gallery);
          }
        }
      })
      .catch((error) => {
        setIsLoading(false);
        handleApiError(error);
      });
  };

  const loadGallery = async (galleryIds) => {
    setIsLoading(true);

    let galleryPromisesArr = [];

    for (let i = 0; i < galleryIds.length; i++) {
      galleryPromisesArr.push(
        new Promise((resolve, reject) => {
          galleryViewByIdService(galleryIds[i])
            .then((response) => {
              if (response.data.items && response.data.items.length > 0) {
                resolve(response.data.items[0]);
              }
            })
            .catch((err) => {
              reject(err);
            });
        }), // Promise - Push
      );
    }

    let photosPromisesArr = [];

    await Promise.all(galleryPromisesArr).then(async (galleries) => {
      for (let i = 0; i < galleries.length; i++) {
        photosPromisesArr.push(
          new Promise((resolve, reject) => {
            photoListByGalleryIdService(galleries[i]._id).then((response) => {
              if (response.data.items && response.data.items.length > 0) {
                const photoItem = response.data.items.map((photo) => {
                  return {
                    src: photo.photoUrl,
                    thumbnail: photo.thumbnailUrl,
                    thumbnailCaption: photo.title,
                    caption: photo.description,
                  };
                }); // map
                resolve({ ...galleries[i], photos: photoItem });
              } // ID
            });
          }), // Promise
        ); // push
      } // for
    }); // Promise

    await Promise.all(photosPromisesArr)
      .then((galleryAndPhotos) => {
        setGallery(galleryAndPhotos);
        setIsLoading(false);
      })
      .catch((err) => {
        setIsLoading(false);
      });
  };

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });

  var breadcrumb = [];
  breadcrumb.push({
    label: 'Knowledge Base View',
    path: '/knowledge-base/view',
  });
  if (post.categories) {
    let categoryName = [];
    if (post.categories && post.categories.length > 0) {
      post.categories.map((item, index) => {
        breadcrumb.push({
          label: item.parent.name,
          path: '/knowledge-base/view',
        });
        categoryName.push(item.name);
      });
      categoryName = categoryName.join(', ');
    }
    breadcrumb.push({ label: categoryName, path: '/back' });
  }
  breadcrumb.push({ label: post.title, path: '' });

  const GalleryPlacement = ({ galleryItems }) => {
    if (galleryItems && galleryItems.length > 0) {
      let html = galleryItems.map((galleryItem) => {
        return (
          <>
            <Row>
              <Col span={24}>
                <h1>{galleryItem.title}</h1>
                <p>{galleryItem.description}</p>
                <Gallery images={galleryItem.photos} />
              </Col>
            </Row>
            <Space>&nbsp;</Space>
          </>
        );
      });
      return <div>HTML{html}</div>;
    } else {
      return null;
    }
  };

  const EditButton = () => {
    if (
      post.client === activeClient._id &&
      accessControls.can(permissions, 'KBPost.update:any')
    ) {
      return (
        <Button
          key="1"
          onClick={(e) => {
            history.push('/knowledge-base/update/' + post._id);
          }}
          type="primary"
          shape="round"
          icon={<EditOutlined />}
        >
          Edit
        </Button>
      );
    } else {
      return null;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const months = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ];

    const day = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear();

    let hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'

    return `${day} ${month} ${year}, ${hours}:${minutes} ${ampm}`;
  };

  // Convert an inline style string (e.g. from TinyMCE content) into a React
  // style object, since React.createElement rejects string style props
  const inlineStyleToObject = (styleString) => {
    if (!styleString || typeof styleString !== 'string') return undefined;
    return styleString.split(';').reduce((styleObject, declaration) => {
      const separatorIndex = declaration.indexOf(':');
      if (separatorIndex === -1) return styleObject;
      const property = declaration
        .slice(0, separatorIndex)
        .trim()
        .replace(/^-ms-/, 'ms-')
        .replace(/-(.)/g, (_, character) => character.toUpperCase());
      const value = declaration.slice(separatorIndex + 1).trim();
      if (property && value) {
        styleObject[property] = value;
      }
      return styleObject;
    }, {});
  };

  return (
    <div>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{post.title}</title>
      </Helmet>
      <MyBreadcrumb items={breadcrumb} />
      <PageHeader
        ghost={false}
        onBack={() => window.history.back()}
        title={post.title}
        extra={[
          <EditButton />,
          <Button
            key="1"
            onClick={handlePrint}
            type="primary"
            shape="round"
            icon={<PrinterOutlined />}
          >
            Print
          </Button>,
        ]}
      >
        <Spin spinning={isLoading} />
        <div className="kbPostViewDetail" ref={componentRef}>
          <Title className="showOnlyForPrint">{post.title}</Title>
          <div className="kbPostViewDetailContent">
            <div
              style={{
                marginBottom: '20px',
                color: '#666',
                fontSize: '14px',
                backgroundColor: '#f5f5f5',
                padding: '12px',
                borderRadius: '4px',
              }}
            >
              <Row gutter={[16, 8]}>
                <Col span={12}>
                  <div style={{ marginBottom: '8px' }}>
                    <span style={{ color: '#8c8c8c', marginRight: '8px' }}>
                      Created:
                    </span>
                    <span>
                      {post.created_at ? formatDate(post.created_at) : 'N/A'}
                    </span>
                  </div>
                  <div>
                    <span style={{ color: '#8c8c8c', marginRight: '8px' }}>
                      By:
                    </span>
                    <span>{post.created_by?.display_name || 'N/A'}</span>
                  </div>
                </Col>
                <Col span={12}>
                  <div style={{ marginBottom: '8px' }}>
                    <span style={{ color: '#8c8c8c', marginRight: '8px' }}>
                      Updated:
                    </span>
                    <span>
                      {post.updated_at ? formatDate(post.updated_at) : 'N/A'}
                    </span>
                  </div>
                  <div>
                    <span style={{ color: '#8c8c8c', marginRight: '8px' }}>
                      By:
                    </span>
                    <span>{post.updated_by?.display_name || 'N/A'}</span>
                  </div>
                </Col>
              </Row>
            </div>
            {post.gallery_placement === 'BEFORE' && (
              <GalleryPlacement galleryItems={gallery} />
            )}
            {post.content && post.content.length > 0 && (
              <div>
                {ReactHtmlParser(post.content, {
                  transform: (node, index) => {
                    // Ensure code and pre tags render safely without executing HTML
                    if (
                      node.type === 'tag' &&
                      (node.name === 'code' || node.name === 'pre')
                    ) {
                      // Extract all text content recursively
                      const extractText = (children) => {
                        if (!children) return '';
                        return children
                          .map((child) => {
                            if (child.type === 'text') {
                              return child.data;
                            } else if (child.children) {
                              return extractText(child.children);
                            }
                            return '';
                          })
                          .join('');
                      };

                      const textContent = extractText(node.children);
                      // Return the code/pre element with text content only (no nested HTML execution)
                      const { style, ...attribs } = node.attribs || {};
                      return React.createElement(
                        node.name,
                        {
                          ...attribs,
                          style: inlineStyleToObject(style),
                          key: `code-${index}`,
                        },
                        textContent,
                      );
                    }
                    return undefined;
                  },
                })}
              </div>
            )}
            {post.markdown_content &&
              post.markdown_content.trim().length > 0 && (
                <div>
                  <Markdown
                    options={{
                      overrides: {
                        h1: {
                          component: Typography.Title,
                          props: { level: 1 },
                        },
                        h2: {
                          component: Typography.Title,
                          props: { level: 2 },
                        },
                        h3: {
                          component: Typography.Title,
                          props: { level: 3 },
                        },
                        h4: {
                          component: Typography.Title,
                          props: { level: 4 },
                        },
                        h5: {
                          component: Typography.Title,
                          props: { level: 5 },
                        },
                        code: {
                          component: 'code',
                          props: {
                            style: {
                              backgroundColor: '#f5f5f5',
                              padding: '2px 6px',
                              borderRadius: '3px',
                              fontFamily: 'monospace',
                              fontSize: '0.9em',
                            },
                          },
                        },
                        pre: {
                          component: 'pre',
                          props: {
                            style: {
                              backgroundColor: '#f5f5f5',
                              padding: '12px',
                              borderRadius: '4px',
                              overflow: 'auto',
                              border: '1px solid #e8e8e8',
                            },
                          },
                        },
                        table: {
                          component: 'table',
                          props: {
                            style: {
                              borderCollapse: 'collapse',
                              width: '100%',
                              margin: '16px 0',
                            },
                          },
                        },
                        th: {
                          component: 'th',
                          props: {
                            style: {
                              border: '1px solid #ddd',
                              padding: '8px',
                              backgroundColor: '#f2f2f2',
                              textAlign: 'left',
                            },
                          },
                        },
                        td: {
                          component: 'td',
                          props: {
                            style: {
                              border: '1px solid #ddd',
                              padding: '8px',
                            },
                          },
                        },
                        blockquote: {
                          component: 'blockquote',
                          props: {
                            style: {
                              borderLeft: '4px solid #ddd',
                              margin: '16px 0',
                              paddingLeft: '16px',
                              color: '#666',
                              fontStyle: 'italic',
                            },
                          },
                        },
                        a: {
                          component: 'a',
                          props: {
                            target: '_blank',
                            rel: 'noopener noreferrer',
                            style: {
                              color: '#1890ff',
                              textDecoration: 'none',
                            },
                          },
                        },
                      },
                    }}
                  >
                    {post.markdown_content}
                  </Markdown>
                </div>
              )}
            {post.gallery_placement === 'AFTER' && (
              <GalleryPlacement galleryItems={gallery} />
            )}
          </div>
        </div>
      </PageHeader>
    </div>
  );
}
